import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/stores/appStore';
import { trendsApi, type TrendConfig } from '@/lib/api';
import { useBackgroundRefresh } from '@/hooks/useBackgroundRefresh';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import {
  Plus,
  X,
  Loader2,
  RefreshCw,
  Sparkles,
  TrendingUp,
  ExternalLink,
  Check,
} from 'lucide-react';

// Mirror the API's PATCH /api/trends/config caps.
const MAX_SEEDS = 20;
const MAX_HANDLES = 10;

/** Platforms that surface trends via competitor monitoring need handle inputs. */
const COMPETITOR_PLATFORMS = ['x', 'facebook', 'linkedin'];

export function SettingsTrends() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ws = currentWorkspace;

  const platformsQuery = useQuery({
    queryKey: ['trend-platforms'],
    queryFn: async () => trendsApi.platforms((await getToken())!),
  });

  const configQuery = useQuery({
    queryKey: ['trend-config', ws?.id],
    enabled: !!ws,
    queryFn: async () => trendsApi.getConfig((await getToken())!, ws!.id),
  });

  const signalsQuery = useQuery({
    queryKey: ['trend-signals', ws?.id],
    enabled: !!ws,
    queryFn: async () => trendsApi.signals((await getToken())!, ws!.id, { limit: 24 }),
  });

  const recsQuery = useQuery({
    queryKey: ['trend-recs', ws?.id],
    enabled: !!ws,
    queryFn: async () => trendsApi.recommendations((await getToken())!, ws!.id),
  });

  // Local editable copy of the config.
  const [form, setForm] = useState<TrendConfig | null>(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [newHashtag, setNewHashtag] = useState('');
  const [refreshNote, setRefreshNote] = useState<string | null>(null);

  useEffect(() => {
    if (configQuery.data?.config) setForm(configQuery.data.config);
  }, [configQuery.data?.config, ws?.id]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form) return;
      return trendsApi.updateConfig((await getToken())!, ws!.id, {
        enabled: form.enabled,
        platforms: form.platforms,
        region: form.region || 'US',
        keyword_seeds: form.keyword_seeds,
        hashtag_seeds: form.hashtag_seeds,
        competitor_handles: form.competitor_handles,
        inject_into_ai: form.inject_into_ai,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trend-config', ws?.id] }),
  });

  const generate = useMutation({
    mutationFn: async () => trendsApi.generateRecommendations((await getToken())!, ws!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trend-recs', ws?.id] }),
  });

  // Refresh returns instantly (the harvest + idea generation run on TREND_QUEUE
  // in the background); we poll signals/recs/config until they land.
  const refresh = useBackgroundRefresh({
    trigger: async () => {
      const token = (await getToken())!;
      // Persist the current form first — the harvest reads config from the DB,
      // so an unsaved enable/platform/keyword change would otherwise be ignored.
      if (form) {
        await trendsApi.updateConfig(token, ws!.id, {
          enabled: form.enabled,
          platforms: form.platforms,
          region: form.region || 'US',
          keyword_seeds: form.keyword_seeds,
          hashtag_seeds: form.hashtag_seeds,
          competitor_handles: form.competitor_handles,
          inject_into_ai: form.inject_into_ai,
        });
      }
      // The server collapses repeat refreshes (e.g. the save above just enabled
      // trends and already queued a harvest) into one billed run.
      const res = await trendsApi.refresh(token, ws!.id, true);
      setRefreshNote(res.alreadyQueued ? (res.message ?? null) : null);
      return res;
    },
    queryKeys: [
      ['trend-signals', ws?.id],
      ['trend-recs', ws?.id],
      ['trend-config', ws?.id],
    ],
    durationMs: 30_000,
  });

  const setRecStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'saved' | 'dismissed' | 'used' }) =>
      trendsApi.setRecommendationStatus((await getToken())!, id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trend-recs', ws?.id] }),
  });

  if (!ws) return <p className="text-sm text-muted-foreground">No workspace selected</p>;
  if (!form) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;

  const support = platformsQuery.data?.supported ?? [];
  const configured = platformsQuery.data?.configured ?? configQuery.data?.configured ?? false;
  const signals = signalsQuery.data?.signals ?? [];
  const recs = recsQuery.data?.recommendations ?? [];

  const togglePlatform = (p: string) => {
    setForm((f) =>
      !f
        ? f
        : {
            ...f,
            platforms: f.platforms.includes(p)
              ? f.platforms.filter((x) => x !== p)
              : [...f.platforms, p],
          }
    );
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" />
          Trend Intelligence
        </h2>
        <p className="text-xs text-muted-foreground">
          Powered by ScrapeCreators. Harvests real-time trends weekly and steers this
          workspace's AI content recommendations.
        </p>
      </div>

      {!configured && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-xs text-foreground">
          The <code className="font-mono">SCRAPE_CREATORS_API_KEY</code> secret is not set on the
          API. Trends can be configured but won't refresh until a key is added
          (<span className="text-accent">wrangler secret put SCRAPE_CREATORS_API_KEY</span>).
        </div>
      )}

      {/* Enable + AI injection */}
      <div className="space-y-3">
        <Toggle
          label="Enable trend harvesting"
          description="Pull weekly trending content for the platforms below."
          checked={form.enabled}
          onChange={(v) => setForm({ ...form, enabled: v })}
        />
        <Toggle
          label="Steer AI recommendations"
          description="Inject discovered trends into every AI prompt and the For You content planner."
          checked={form.inject_into_ai}
          onChange={(v) => setForm({ ...form, inject_into_ai: v })}
        />
      </div>

      {/* Platforms */}
      <Field label="Platforms" hint="which networks to harvest trends for">
        <div className="flex flex-wrap gap-2">
          {support.map((p) => {
            const active = form.platforms.includes(p.platform);
            return (
              <button
                key={p.platform}
                disabled={p.future}
                onClick={() => togglePlatform(p.platform)}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                  p.future
                    ? 'border-border text-muted-foreground/60 cursor-not-allowed'
                    : active
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border text-muted-foreground hover:text-foreground',
                ].join(' ')}
                title={p.future ? 'Coming soon' : p.discovery.join(', ')}
              >
                <PlatformIcon
                  platform={p.platform}
                  variant="glyph"
                  size="xs"
                  className={p.future ? 'opacity-50' : undefined}
                />
                <span>
                  {p.label}
                  {p.future && <span className="ml-1 opacity-70">(soon)</span>}
                </span>
              </button>
            );
          })}
        </div>
      </Field>

      {/* Region */}
      <Field label="Region" hint="2-letter code for region-scoped trending feeds">
        <input
          value={form.region}
          onChange={(e) => setForm({ ...form, region: e.target.value.toUpperCase().slice(0, 2) })}
          placeholder="US"
          className="w-24 bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
        />
      </Field>

      {/* Keyword seeds */}
      <TagField
        label="Keyword seeds"
        hint="topics to discover popular content for (TikTok / YouTube / LinkedIn search)"
        placeholder="Add a keyword..."
        value={form.keyword_seeds}
        max={MAX_SEEDS}
        newValue={newKeyword}
        setNewValue={setNewKeyword}
        onAdd={(v) => setForm({ ...form, keyword_seeds: [...form.keyword_seeds, v] })}
        onRemove={(v) =>
          setForm({ ...form, keyword_seeds: form.keyword_seeds.filter((x) => x !== v) })
        }
      />

      {/* Hashtag seeds */}
      <TagField
        label="Hashtag seeds"
        hint="hashtags to track (Instagram / TikTok)"
        placeholder="#hashtag"
        value={form.hashtag_seeds}
        max={MAX_SEEDS}
        newValue={newHashtag}
        setNewValue={setNewHashtag}
        onAdd={(v) =>
          setForm({ ...form, hashtag_seeds: [...form.hashtag_seeds, v.replace(/^#?/, '#')] })
        }
        onRemove={(v) =>
          setForm({ ...form, hashtag_seeds: form.hashtag_seeds.filter((x) => x !== v) })
        }
      />

      {/* Competitor handles for platforms without a native trending feed */}
      <Field
        label="Competitor accounts"
        hint="X / Facebook / LinkedIn have no trending feed — we monitor these accounts instead"
      >
        <div className="space-y-2">
          {COMPETITOR_PLATFORMS.map((p) => (
            <div key={p} className="flex items-center gap-2">
              <span className="flex w-20 shrink-0 items-center gap-1.5 text-xs text-muted-foreground capitalize">
                <PlatformIcon platform={p} variant="glyph" size="xs" />
                {p}
              </span>
              <input
                value={(form.competitor_handles[p] ?? []).join(', ')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    competitor_handles: {
                      ...form.competitor_handles,
                      [p]: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .slice(0, MAX_HANDLES),
                    },
                  })
                }
                placeholder={`handle1, handle2 (up to ${MAX_HANDLES})`}
                className="flex-1 bg-input border border-border rounded-md px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          ))}
        </div>
      </Field>

      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm hover:bg-accent/90 disabled:opacity-50"
        >
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save Settings
        </button>
        <button
          onClick={() => refresh.start()}
          disabled={refresh.isRefreshing || !form.enabled || !configured}
          className="flex items-center gap-2 px-4 py-2 bg-muted/60 text-foreground border border-border rounded-md text-sm hover:bg-muted/80 disabled:opacity-50"
          title={!configured ? 'API key not configured' : 'Harvest trends and generate ideas now'}
        >
          {refresh.isRefreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh now
        </button>
        {refresh.isRefreshing ? (
          <span className="text-xs text-muted-foreground">
            {refreshNote ?? 'Harvesting trends & generating ideas in the background…'}
          </span>
        ) : form.last_refreshed_at ? (
          <span className="text-xs text-muted-foreground">
            Last refreshed {formatUtc(form.last_refreshed_at)}
          </span>
        ) : null}
        {refresh.isError && (
          <span className="text-xs text-destructive">{refresh.error?.message || 'Refresh failed.'}</span>
        )}
        {save.isError && (
          <span className="text-xs text-destructive">{save.error.message}</span>
        )}
      </div>
      {configQuery.data?.config.last_error && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-xs text-foreground">
          The last harvest
          {configQuery.data.config.last_error_at
            ? ` (${formatUtc(configQuery.data.config.last_error_at)})`
            : ''}{' '}
          ran into problems: {configQuery.data.config.last_error}
        </div>
      )}

      {/* Content ideas */}
      <section className="pt-4 border-t border-hairline space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            Trend-driven content ideas
          </h3>
          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending || signals.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/60 text-foreground border border-border rounded-md text-xs hover:bg-muted/80 disabled:opacity-50"
          >
            {generate.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Generate ideas
          </button>
        </div>

        {generate.isPending || (refresh.isRefreshing && recs.length === 0) ? (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Generating ideas…
          </p>
        ) : recs.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No ideas yet. Refresh trends, then generate ideas.
          </p>
        ) : (
          <div className="space-y-2">
            {recs.map((r) => {
              const tags: string[] = (() => {
                try {
                  return r.suggested_hashtags ? JSON.parse(r.suggested_hashtags) : [];
                } catch {
                  return [];
                }
              })();
              return (
                <div
                  key={r.id}
                  className="rounded-md border border-hairline bg-card px-4 py-3 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {r.platform && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-accent font-medium">
                          <PlatformIcon platform={r.platform} variant="glyph" size="xs" />
                          {r.platform}
                        </span>
                      )}
                      <span className="text-sm font-medium text-foreground">{r.title}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setRecStatus.mutate({ id: r.id, status: 'saved' })}
                        className="p-1 text-muted-foreground hover:text-success"
                        title="Save"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setRecStatus.mutate({ id: r.id, status: 'dismissed' })}
                        className="p-1 text-muted-foreground hover:text-danger"
                        title="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-foreground/90">{r.idea}</p>
                  {r.rationale && (
                    <p className="text-xs text-muted-foreground italic">{r.rationale}</p>
                  )}
                  {tags.length > 0 && (
                    <p className="text-xs text-accent">{tags.join(' ')}</p>
                  )}
                  <div className="pt-1">
                    <button
                      onClick={() => {
                        setRecStatus.mutate({ id: r.id, status: 'used' });
                        navigate('/publishing?compose=true', {
                          state: {
                            prefillContent: `${r.idea}${tags.length ? `\n\n${tags.join(' ')}` : ''}`,
                          },
                        });
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-accent/15 text-accent ring-1 ring-accent/30 rounded-md hover:bg-accent/25 transition-colors"
                    >
                      <Sparkles className="h-3 w-3" />
                      Use this idea
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Raw signals preview */}
      <section className="pt-4 border-t border-hairline space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Trending now</h3>
        {signals.length === 0 ? (
          <p className="text-xs text-muted-foreground">No signals harvested for this week yet.</p>
        ) : (
          <div className="space-y-1">
            {signals.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-2 text-xs py-1.5 border-b border-hairline/50"
              >
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground w-28 shrink-0">
                  <PlatformIcon platform={s.platform} variant="glyph" size="xs" />
                  <span className="min-w-0 break-words">
                    {s.platform}/{s.signal_type}
                  </span>
                </span>
                <span className="flex-1 truncate text-foreground" title={s.title ?? ''}>
                  {s.title ?? '(untitled)'}
                </span>
                {s.velocity != null && s.velocity >= 0.5 && (
                  <span
                    className="flex items-center gap-0.5 text-[10px] font-semibold text-success bg-success/10 px-1.5 py-0.5 rounded-full shrink-0"
                    title={`Engagement up ${Math.round(s.velocity * 100)}% week-over-week`}
                  >
                    <TrendingUp className="h-2.5 w-2.5" />
                    +{Math.round(s.velocity * 100)}%
                  </span>
                )}
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {formatMetric(s.metric_views || s.metric_likes)}
                </span>
                {s.url && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** D1 timestamps are UTC; older ones lack a zone marker and would parse as local time. */
function formatUtc(ts: string): string {
  const iso = /(?:[zZ]|[+-]\d\d:?\d\d)$/.test(ts) ? ts : `${ts.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

function formatMetric(n: number): string {
  if (!n) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground block mb-1.5">
        {label}
        {hint && <span className="text-muted-foreground font-normal ml-1">({hint})</span>}
      </label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="flex items-start gap-3 text-left w-full"
    >
      <span
        className={[
          'mt-0.5 h-5 w-9 rounded-full relative shrink-0 transition-colors',
          checked ? 'bg-accent' : 'bg-tertiary ring-1 ring-border',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
            checked ? 'left-[18px]' : 'left-0.5',
          ].join(' ')}
        />
      </span>
      <span>
        <span className="text-sm text-foreground block">{label}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </span>
    </button>
  );
}

function TagField({
  label,
  hint,
  placeholder,
  value,
  max,
  newValue,
  setNewValue,
  onAdd,
  onRemove,
}: {
  label: string;
  hint?: string;
  placeholder: string;
  value: string[];
  max: number;
  newValue: string;
  setNewValue: (v: string) => void;
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
}) {
  const full = value.length >= max;
  const add = () => {
    const v = newValue.trim();
    if (v && !full && !value.includes(v)) onAdd(v);
    setNewValue('');
  };
  return (
    <Field label={label} hint={full ? `${hint ? `${hint} · ` : ''}limit of ${max} reached` : hint}>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((v) => (
          <div key={v} className="flex items-center gap-1 bg-tertiary ring-1 ring-border rounded-full px-3 py-1">
            <span className="text-xs text-foreground">{v}</span>
            <button onClick={() => onRemove(v)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          // Commit pending text when focus leaves (e.g. the user typed a seed
          // then clicked Save/Refresh without pressing Enter).
          onBlur={add}
          disabled={full}
          placeholder={full ? `Up to ${max}` : placeholder}
          className="flex-1 bg-input border border-border rounded-md px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
        />
        <button onClick={add} disabled={full} className="p-2 bg-tertiary ring-1 ring-border rounded-md text-muted-foreground hover:text-foreground disabled:opacity-50">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </Field>
  );
}
